<script setup>
import BasePreferenceTemplate from '@/components/preference/templates/BasePreferenceTemplate.vue'
import ModalEmailEdit from '@/components/edit/ModalEmailEdit.vue'
import PreferenceLogo from '@/components/preference/PreferenceLogo.vue'
import EmailprefBadge from '@/components/EmailprefBadge.vue'
import CosmicEarth from '@/components/preference/templates/CosmicEarth.vue'

import { computed } from 'vue'
import { withProps } from '@/components/preference/templates/preferenceTemplate.js'

const props = defineProps({ ...withProps() })
defineEmits(['save', 'userEmail', 'update'])
defineOptions({ inheritAttrs: false })

const cosmicAttrs = computed(() => ({
  ...props.attrs,
  primary_color: props.attrs?.primary_color || '#06b6d4',
}))
</script>

<template>
  <BasePreferenceTemplate
    v-bind="{ ...$props, ...$attrs }"
    outer-class="cosmic-bg relative overflow-hidden"
    :outer-style="{ backgroundColor: undefined }"
    :card-component="'div'"
    card-class="cosmic-card w-full max-w-lg mx-4 p-8 rounded-2xl relative"
    content-class="space-y-6"
    main-class="cosmic-list"
    :list-attributes="cosmicAttrs"
    @save="$emit('save')"
    @user-email="$emit('userEmail', $event)"
    @update="$emit('update')"
  >
    <template #background>
      <div v-if="attrs?.background_color" class="absolute inset-0 opacity-100 mix-blend-overlay" :style="{ backgroundColor: attrs.background_color }"></div>
      <CosmicEarth />
    </template>

    <template #form-step="{ logo, attrs: a, name, chosenEmail, updateChosenEmail, alertMsg, setEmail }">
      <PreferenceLogo :url="logo" />
      <div class="text-center">
        <h1 class="text-2xl font-bold mt-4">{{ a?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h1>
        <h2 class="text-cyan-300/70 mt-1">{{ a?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</h2>
      </div>

      <form class="flex flex-col items-center space-y-4" @submit="setEmail($event, chosenEmail)">
        <div class="w-full px-2">
          <label for="email" class="block text-sm font-medium text-cyan-200 mb-1">{{ $t('pref_page_email_form.form_title') }}</label>
          <input
            id="email"
            :value="chosenEmail"
            type="email"
            name="email"
            class="block w-full rounded-lg border border-white/20 bg-white/10 py-2.5 px-3 text-white placeholder:text-white/40 focus:ring-2 focus:ring-cyan-400 focus:border-transparent backdrop-blur-sm sm:text-sm"
            :placeholder="$t('pref_page_email_form.form_placeholder').replace('[at]', '@')"
            @input="updateChosenEmail($event.target.value)"
          />
          <span v-if="alertMsg" class="text-red-400 text-xs mt-1 block">{{ alertMsg }}</span>
        </div>
        <button
          type="submit"
          class="cosmic-btn w-[90%] rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-lg"
          :style="{ backgroundImage: `linear-gradient(135deg, ${a?.primary_color || '#06b6d4'}, ${a?.primary_color || '#06b6d4'}88, #3b82f6)` }"
        >
          {{ $t('pref_page_email_form.form_save_btn') }}
        </button>
      </form>
    </template>

    <template #preferences-header="{ logo, attrs: a, name }">
      <PreferenceLogo :url="logo" />
      <div class="text-center mt-4">
        <h2 class="text-2xl font-bold">{{ a?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h2>
        <p class="text-cyan-300/70 mt-1">{{ a?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</p>
      </div>
    </template>

    <template #save-feedback="{ saved, saveStatus, attrs: a }">
      <!-- Cosmic autosave pill -->
      <div v-if="a?.auto_save" class="fixed top-4 inset-x-0 flex justify-center z-50 pointer-events-none">
        <Transition name="cosmic-autosave">
          <div v-if="saveStatus !== 'idle'" class="cosmic-autosave-pill">
            <span v-if="saveStatus === 'saving'" class="cosmic-autosave-star animate-spin">&#10022;</span>
            <span v-else class="cosmic-autosave-star cosmic-autosave-check">&#10003;</span>
            <span>{{ saveStatus === 'saving' ? 'Saving...' : 'Saved' }}</span>
          </div>
        </Transition>
      </div>

      <!-- Orbiting star save indicator -->
      <Transition name="cosmic-orbit">
        <div v-if="!a?.auto_save && saved" class="cosmic-orbit-container">
          <div class="cosmic-orbit-ring">
            <div class="cosmic-orbit-star">&#10022;</div>
          </div>
          <div class="cosmic-orbit-label">
            {{ a?.saved_preferences_msg || $t('pref_page.save_msg') || 'Saved' }}
          </div>
        </div>
      </Transition>
    </template>

    <template #action-buttons="{ attrs: a, disabled, updateGlobal, savePreferences }">
      <div class="flex justify-center space-x-4 pt-2">
        <button
          v-if="a.unsubscribe_all"
          class="rounded-lg px-5 py-2.5 text-sm font-semibold text-white/80 border border-white/20 hover:bg-white/10 transition-all"
          @click="!disabled ? updateGlobal() : null"
        >
          {{ $t('pref_page.unsubscribe_btn') }}
        </button>
        <button
          v-if="!a?.auto_save"
          class="cosmic-btn rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-lg"
          :style="{ backgroundImage: `linear-gradient(135deg, ${a?.primary_color || '#06b6d4'}, ${a?.primary_color || '#06b6d4'}88, #3b82f6)` }"
          @click="savePreferences"
        >
          {{ $t('pref_page.save_btn') }}
        </button>
      </div>
    </template>

    <template #modal="{ modal, modalType, chosenEmail, customerId, preview, updateEmail, closeModal }">
      <Transition name="cosmic-modal">
        <div v-if="modal && modalType === 'email'" class="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div class="cosmic-modal-panel relative w-full max-w-lg rounded-2xl p-6 sm:p-8">
            <button class="absolute top-4 right-4 text-white/50 hover:text-white transition-colors" @click="closeModal">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                <path
                  fill-rule="evenodd"
                  d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
            <ModalEmailEdit
              :email="chosenEmail"
              :customer-id="customerId"
              :attributes="cosmicAttrs"
              :preview="preview"
              @save="updateEmail"
              @close="closeModal"
            />
          </div>
        </div>
      </Transition>
    </template>

    <template #badge>
      <div v-if="!attrs?.remove_badge" class="cosmic-badge">
        <EmailprefBadge />
      </div>
    </template>
  </BasePreferenceTemplate>
</template>

<style scoped>
.cosmic-bg {
  background-color: #0a0e27;
  background-image: radial-gradient(ellipse at 50% 0%, rgba(6, 182, 212, 0.08) 0%, transparent 60%);
}

:deep(.cosmic-card) {
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 40px rgba(6, 182, 212, 0.08);
  animation: card-shimmer 6s ease-in-out infinite alternate;
  color: #f1f5f9;
}

@keyframes card-shimmer {
  0% {
    border-color: rgba(255, 255, 255, 0.15);
  }
  100% {
    border-color: rgba(6, 182, 212, 0.3);
  }
}

.cosmic-btn {
  background: linear-gradient(135deg, #06b6d4, #3b82f6);
  transition: all 0.3s ease;
}

.cosmic-btn:hover {
  box-shadow: 0 0 20px rgba(6, 182, 212, 0.4);
  transform: translateY(-1px);
}

:deep(.cosmic-list) h3,
:deep(.cosmic-list) .font-medium {
  color: #f1f5f9;
}

:deep(.cosmic-list) .text-gray-700,
:deep(.cosmic-list) .text-gray-900,
:deep(.cosmic-list) .text-lg {
  color: #e2e8f0;
}

:deep(.cosmic-list) p,
:deep(.cosmic-list) .text-gray-500,
:deep(.cosmic-list) .text-gray-400,
:deep(.cosmic-list) .text-sm {
  color: #cbd5e1;
}

:deep(.cosmic-list) button {
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  --tw-ring-shadow: 0 0 #0000 !important;
  --tw-shadow: 0 0 #0000 !important;
  box-shadow: none !important;
  background-color: transparent;
}

:deep(.cosmic-list) button:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

:deep(.cosmic-list) .border-gray-200,
:deep(.cosmic-list) .border-gray-100 {
  border-color: rgba(255, 255, 255, 0.1);
}

:deep(.cosmic-list) .bg-white {
  background-color: rgba(255, 255, 255, 0.05);
}

.cosmic-badge :deep(div) {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.cosmic-badge :deep(p) {
  color: rgba(255, 255, 255, 0.5);
}

.cosmic-badge :deep(img) {
  filter: brightness(0) invert(1);
  opacity: 0.7;
}

/* --- Cosmic modal panel --- */
.cosmic-modal-panel {
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 40px rgba(6, 182, 212, 0.1);
  color: #f1f5f9;
}

.cosmic-modal-panel :deep(.border) {
  border-color: rgba(255, 255, 255, 0.2);
}

.cosmic-modal-panel :deep(svg) {
  color: #06b6d4;
}

.cosmic-modal-panel :deep(h1),
.cosmic-modal-panel :deep(label) {
  color: #f1f5f9;
}

.cosmic-modal-panel :deep(p) {
  color: #cbd5e1;
}

.cosmic-modal-panel :deep(input) {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  color: #f1f5f9;
}

.cosmic-modal-panel :deep(input::placeholder) {
  color: rgba(255, 255, 255, 0.35);
}

.cosmic-modal-panel :deep(input:focus) {
  border-color: #06b6d4;
  box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.3);
  outline: none;
}

.cosmic-modal-panel :deep(.flex.text-right button:first-child) {
  background-color: transparent !important;
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: none !important;
  --tw-ring-shadow: 0 0 #0000 !important;
  --tw-shadow: 0 0 #0000 !important;
}

.cosmic-modal-panel :deep(.flex.text-right button:first-child:hover) {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.cosmic-modal-panel :deep(.error-text) {
  color: #f87171;
}

/* Cosmic modal transitions */
.cosmic-modal-enter-active {
  transition: opacity 0.3s ease-out;
}

.cosmic-modal-leave-active {
  transition: opacity 0.2s ease-in;
}

.cosmic-modal-enter-from,
.cosmic-modal-leave-to {
  opacity: 0;
}

/* --- Cosmic autosave pill --- */
.cosmic-autosave-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #a5f3fc;
  background: rgba(6, 182, 212, 0.12);
  border: 1px solid rgba(6, 182, 212, 0.3);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 0 12px rgba(6, 182, 212, 0.15);
}

.cosmic-autosave-star {
  font-size: 14px;
  color: #06b6d4;
  filter: drop-shadow(0 0 4px rgba(6, 182, 212, 0.6));
}

.cosmic-autosave-check {
  animation: check-pop 0.3s ease-out;
}

@keyframes check-pop {
  0% {
    transform: scale(0);
  }
  60% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}

.cosmic-autosave-enter-active {
  transition: all 0.25s ease-out;
}

.cosmic-autosave-leave-active {
  transition: all 0.4s ease-in;
}

.cosmic-autosave-enter-from,
.cosmic-autosave-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* --- Orbiting star save indicator --- */
.cosmic-orbit-container {
  position: absolute;
  top: -90px;
  left: 0;
  right: 0;
  pointer-events: none;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cosmic-orbit-ring {
  position: absolute;
  width: 160px;
  height: 160px;
  top: -60px;
  left: 50%;
  margin-left: -80px;
  animation: orbit-spin 2.5s linear infinite;
  transform-style: preserve-3d;
}

.cosmic-orbit-star {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 28px;
  color: #06b6d4;
  filter: drop-shadow(0 0 8px rgba(6, 182, 212, 0.8)) drop-shadow(0 0 20px rgba(6, 182, 212, 0.4));
  animation: star-pulse 0.8s ease-in-out infinite alternate;
}

.cosmic-orbit-label {
  background: rgba(6, 182, 212, 0.15);
  border: 1px solid rgba(6, 182, 212, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #a5f3fc;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 20px;
  border-radius: 9999px;
  white-space: nowrap;
  box-shadow: 0 0 20px rgba(6, 182, 212, 0.2);
  animation: label-glow 1.5s ease-in-out infinite alternate;
}

@keyframes orbit-spin {
  from {
    transform: perspective(600px) rotateX(70deg) rotateZ(0deg);
  }
  to {
    transform: perspective(600px) rotateX(70deg) rotateZ(360deg);
  }
}

@keyframes star-pulse {
  0% {
    transform: translateX(-50%) scale(0.9);
    filter: drop-shadow(0 0 6px rgba(6, 182, 212, 0.6)) drop-shadow(0 0 15px rgba(6, 182, 212, 0.3));
  }
  100% {
    transform: translateX(-50%) scale(1.2);
    filter: drop-shadow(0 0 12px rgba(6, 182, 212, 1)) drop-shadow(0 0 30px rgba(6, 182, 212, 0.6));
  }
}

@keyframes label-glow {
  0% {
    box-shadow: 0 0 10px rgba(6, 182, 212, 0.15);
  }
  100% {
    box-shadow: 0 0 25px rgba(6, 182, 212, 0.35);
  }
}

/* Transition for orbit container */
.cosmic-orbit-enter-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.cosmic-orbit-leave-active {
  transition: all 0.6s ease-in;
}

.cosmic-orbit-enter-from {
  opacity: 0;
  transform: scale(0.5);
}

.cosmic-orbit-leave-to {
  opacity: 0;
  transform: scale(1.3);
}
</style>
